import React, { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  IoReloadOutline, IoSearchOutline, IoCheckmarkCircle,
  IoSaveOutline, IoDocumentText, IoPersonOutline,
} from "react-icons/io5";
import toast from "react-hot-toast";
import axiosAuth from "../../../utils/axios";

const STATUS_OPTIONS = [
  "Received", "Contacted - Waiting Patient Signature", "Ready to Bill",
  "Ready to Ship", "Shipped", "DENIED-READ DETAILS", "Completed",
  "Re-Ship Item", "Need Additional Information", "Medical Records Requested",
  "VOYAGE COMPLETED", "Collect CashPay", "Collect Deposit",
  "Under Review - On Hold", "Check Insurance", "Call Patient",
  "Checking Eligibility", "LVM", "Collect Copay/Coinsurance",
];

const CompressionSockBillerView = () => {
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Per-row draft state for comments + collection
  const [drafts, setDrafts] = useState({});   // { [id]: { comments, collection } }
  const [savingId, setSavingId] = useState(null);

  const fetchOrders = async (mode = "load") => {
    mode === "load" ? setIsLoading(true) : setIsRefreshing(true);
    try {
      const axiosInstance = axiosAuth();
      const res = await axiosInstance.get("orders/compressed-sock/list/?page_size=100");
      if (res?.data?.success) {
        setOrders(res.data.data || []);
      } else {
        toast.error("Failed to load orders");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load orders");
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => { fetchOrders("load"); }, []);

  const handleDraftChange = (id, field, value) => {
    setDrafts(prev => ({
      ...prev,
      [id]: { ...prev[id], [field]: value }
    }));
  };

  const getValue = (order, field) => {
    if (drafts[order.id] && drafts[order.id][field] !== undefined) {
      return drafts[order.id][field];
    }
    return order[field] || "";
  };

  const hasUnsaved = (order) => {
    const draft = drafts[order.id];
    if (!draft) return false;
    const commentsChanged = draft.comments !== undefined && draft.comments !== (order.comments || "");
    const collectionChanged = draft.collection !== undefined && draft.collection !== (order.collection || "");
    return commentsChanged || collectionChanged;
  };

  const handleSave = async (order) => {
    const draft = drafts[order.id] || {};
    const payload = {};
    if (draft.comments !== undefined) payload.comments = draft.comments;
    if (draft.collection !== undefined) payload.collection = draft.collection;

    if (Object.keys(payload).length === 0) {
      toast("Nothing to save");
      return;
    }

    try {
      setSavingId(order.id);
      const axiosInstance = axiosAuth();
      const res = await axiosInstance.patch(
        `orders/compressed-sock/${order.id}/update/`,
        payload
      );
      if (res?.data?.success) {
        toast.success(`✅ Saved for Order #${order.order_number}`);
        // Update local list
        setOrders(prev => prev.map(o => o.id === order.id
          ? { ...o, ...payload }
          : o
        ));
        // Clear draft
        setDrafts(prev => {
          const next = { ...prev };
          delete next[order.id];
          return next;
        });
      } else {
        toast.error("Failed to save");
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.error || "Failed to save");
    } finally {
      setSavingId(null);
    }
  };

  const filtered = useMemo(() => {
    let list = orders;
    if (statusFilter !== "all") {
      list = list.filter(o => o.compression_sock_status === statusFilter);
    }
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(o =>
        (o.patient_name_or_id || "").toLowerCase().includes(q) ||
        (o.order_number || "").toLowerCase().includes(q) ||
        (o.physician || "").toLowerCase().includes(q) ||
        (o.hcpc || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [orders, search, statusFilter]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-white via-teal-50/40 to-emerald-50/30 flex items-center justify-center">
        <div className="rounded-2xl border border-teal-100 bg-white/70 p-8 shadow-sm backdrop-blur flex items-center gap-4">
          <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="h-10 w-10 rounded-full border-4 border-teal-100 border-t-teal-600" />
          <div>
            <p className="text-sm font-semibold text-slate-900">Loading compression sock orders</p>
            <p className="text-sm text-slate-600">Fetching data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-teal-50/40 to-emerald-50/30">
      <div className="mx-auto max-w-[1400px] px-6 py-8">

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-teal-200 bg-white/70 px-3 py-1 text-xs font-semibold text-teal-700 shadow-sm backdrop-blur">
                <span className="h-2 w-2 rounded-full bg-teal-500" />
                Biller Console • Compression Socks
              </div>
              <h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                Compression Sock Orders
              </h1>
              <p className="mt-2 text-sm text-slate-600 sm:text-base">
                Add comments and collection info for each order.{" "}
                <span className="font-semibold text-slate-700">
                  {filtered.length} {filtered.length === 1 ? "order" : "orders"}.
                </span>
              </p>
            </div>

            <button
              onClick={() => fetchOrders("refresh")}
              disabled={isRefreshing}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:shadow-md disabled:cursor-not-allowed disabled:opacity-60"
            >
              <motion.span
                animate={isRefreshing ? { rotate: 360 } : { rotate: 0 }}
                transition={isRefreshing ? { duration: 0.9, repeat: Infinity, ease: "linear" } : { duration: 0.2 }}
                className="inline-flex"
              >
                <IoReloadOutline className="text-teal-600" />
              </motion.span>
              {isRefreshing ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        </motion.div>

        {/* Filters */}
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1 max-w-md">
            <IoSearchOutline className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search patient, order #, physician..."
              className="w-full rounded-xl border border-slate-200 bg-white pl-10 pr-3 py-2.5 text-sm shadow-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm shadow-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
          >
            <option value="all">All statuses</option>
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        {/* List */}
        {filtered.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 bg-white/70 p-10 text-center shadow-sm backdrop-blur">
            <IoDocumentText className="text-3xl text-slate-400 mx-auto mb-2" />
            <p className="text-lg font-bold text-slate-900">No orders found</p>
            <p className="text-sm text-slate-600 mt-1">Try adjusting filters.</p>
          </div>
        ) : (
          <div className="space-y-4">
            <AnimatePresence>
              {filtered.map((order, i) => (
                <motion.div
                  key={order.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -40 }}
                  transition={{ delay: i * 0.02 }}
                  className="rounded-2xl border border-slate-200 bg-white shadow-sm hover:shadow-md transition overflow-hidden"
                >
                  <div className="p-5">
                    {/* Header row */}
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                      <span className="inline-flex items-center gap-2 rounded-full bg-teal-50 px-3 py-1 text-xs font-bold text-teal-700 ring-1 ring-teal-200">
                        #{order.order_number}
                      </span>
                      {order.compression_sock_status && (
                        <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-700">
                          {order.compression_sock_status}
                        </span>
                      )}
                      {order.medical_records_status && (
                        <span className="inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-1 text-xs font-bold text-indigo-700 ring-1 ring-indigo-200">
                          {order.medical_records_status}
                        </span>
                      )}
                    </div>

                    {/* Prefetched (read-only) details */}
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-5">
                      <ReadField label="Patient" value={order.patient_name_or_id} icon={<IoPersonOutline />} />
                      <ReadField label="DOB" value={order.patient_dob} />
                      <ReadField label="Phone" value={order.phone_number} />
                      <ReadField label="Email" value={order.email_address} />
                      <ReadField label="Physician" value={order.physician} />
                      <ReadField label="Location" value={order.doctor_location} />
                      <ReadField label="Primary Ins." value={order.primary_insurance} />
                      <ReadField label="Member ID" value={order.primary_member_id} />
                      <ReadField label="Dx 1" value={order.icd10_code} />
                      <ReadField label="Dx 2" value={order.dx_code_2} />
                      <ReadField label="HCPC" value={order.hcpc} />
                      <ReadField label="Product" value={order.product_sku} />
                      <ReadField label="Size" value={order.size} />
                      <ReadField label="Ankle" value={order.ankle_measurement} />
                      <ReadField label="Calf" value={order.calf_measurement} />
                      <ReadField label="Thigh" value={order.thigh_measurement} />
                      <ReadField label="Date Received" value={order.date_order_received} />
                      <ReadField label="Sent to Billing" value={order.date_sent_to_billing} />
                    </div>

                    {/* Editable biller fields */}
                    <div className="border-t border-slate-100 pt-5">
                      <p className="text-xs font-bold uppercase tracking-wide text-teal-700 mb-3">
                        Biller inputs
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                            Comments
                          </label>
                          <textarea
                            value={getValue(order, "comments")}
                            onChange={(e) => handleDraftChange(order.id, "comments", e.target.value)}
                            rows={3}
                            placeholder="Add comments..."
                            className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm transition focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                            Collection
                          </label>
                          <input
                            type="text"
                            value={getValue(order, "collection")}
                            onChange={(e) => handleDraftChange(order.id, "collection", e.target.value)}
                            placeholder='e.g. $250.00 / Paid / Pending'
                            className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm transition focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                          />
                          <p className="mt-1 text-xs text-slate-400">
                            Free text — amount, status, or note.
                          </p>
                        </div>
                      </div>

                      <div className="mt-4 flex justify-end">
                        <button
                          onClick={() => handleSave(order)}
                          disabled={!hasUnsaved(order) || savingId === order.id}
                          className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-teal-600 to-emerald-600 px-4 py-2 text-sm font-extrabold text-white shadow-sm transition hover:from-teal-700 hover:to-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {savingId === order.id ? (
                            <>
                              <motion.span
                                animate={{ rotate: 360 }}
                                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                className="inline-flex"
                              >
                                <span className="h-4 w-4 rounded-full border-2 border-white border-t-transparent" />
                              </motion.span>
                              Saving...
                            </>
                          ) : (
                            <>
                              <IoSaveOutline className="text-base" />
                              {hasUnsaved(order) ? "Save changes" : "Saved"}
                              {!hasUnsaved(order) && <IoCheckmarkCircle className="text-base" />}
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
};

const ReadField = ({ label, value, icon }) => (
  <div className="rounded-lg border border-slate-100 bg-slate-50/50 px-3 py-2">
    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1">
      {icon}{label}
    </p>
    <p className="text-sm font-semibold text-slate-900 truncate">
      {value || <span className="text-slate-300 font-normal">—</span>}
    </p>
  </div>
);

export default CompressionSockBillerView;